import { PropsWithChildren } from 'react';

import { formatMoney } from '@betterment/core';
import { Box, DropdownMenu, Icon, IconButton, ListRow, Separator, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';

import { urlBuilder } from '../../../../Shared/urlBuilder';
import { PurposeAccountsCardQuery } from '../graphql/purposeAccountsCard.generated';

type ExternalAccount = Extract<
  PurposeAccountsCardQuery['purpose']['envelope']['accounts'][number],
  { __typename: 'ExternalAccountEnvelopeAssociation' }
>['account'];

export const ExternalAccountRow: React.FC<{ account: ExternalAccount }> = ({ account }) => {
  return (
    <Box className="group">
      <ExternalAccountLinkRow account={account}>
        <ListRow.LeadingAsset>
          <Box css={{ width: '36px', height: '36px' }}>
            <ThemedImage themedAsset={account.icon} alt="" />
          </Box>
        </ListRow.LeadingAsset>
        <ListRow.LeadingContent>
          <Text color="text">{account.name}</Text>
          <Text color="text.weak" variant="text50">
            {account.institutionName}
          </Text>
        </ListRow.LeadingContent>
        <ListRow.TrailingContent css={{ alignItems: 'end' }}>
          <Text color="text">{formatMoney(account.balance)}</Text>
          <Text color="text.weak" variant="text50">
            {account.lastUpdatedAtDisplay}
          </Text>
        </ListRow.TrailingContent>
      </ExternalAccountLinkRow>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
    </Box>
  );
};

function externalAccountLinkOptions(account: ExternalAccount) {
  const isMyExternalAccount = account.__typename === 'ExternalAccount';

  const hasHoldings = isMyExternalAccount && account.hasHoldings;

  const transactionOption = (() => {
    if (!isMyExternalAccount) return null;

    const { legacyId: externalAccountId, transactionOption: option } = account;

    switch (option?.__typename) {
      case 'RolloverOption':
        return {
          url: urlBuilder.appExternalAccountsRolloverStart.absolute({ externalAccountId }),
          title: option.isPartiallyAcatsable ? 'Start rollover' : 'Start transfer',
        };
      case 'ExternalTransferOption':
        return {
          url: urlBuilder.appExternalTaxableAccountTransferFlowsNew.absolute({ externalAccountId }),
          title: 'Start transfer',
        };
      default:
        return null;
    }
  })();

  return {
    transactionOption,
    holdingsOption: hasHoldings
      ? {
          url: urlBuilder.appExternalAccountsHoldings.absolute({ externalAccountId: account.legacyId }),
          title: 'See holdings',
        }
      : null,
    settingsOption: isMyExternalAccount
      ? { url: urlBuilder.appExternalAccounts.absolute(), title: 'See settings' }
      : null,
  };
}

function ExternalAccountLinkRow({ account, children }: PropsWithChildren<{ account: ExternalAccount }>) {
  const { settingsOption, holdingsOption, transactionOption } = externalAccountLinkOptions(account);

  if (!transactionOption && !holdingsOption && !settingsOption) {
    return <ListRow>{children}</ListRow>;
  }

  if (!transactionOption && !holdingsOption) {
    return (
      <SambaAutoLink to={urlBuilder.appExternalAccounts.absolute()} css={{ _hover: { textDecoration: 'none' } }}>
        <ListRow css={{ _groupHover: { cursor: 'pointer' } }}>
          {children}
          <ListRow.TrailingAsset>
            <Icon color="icon" name="CaretRight" size="small" />
          </ListRow.TrailingAsset>
        </ListRow>
      </SambaAutoLink>
    );
  }

  return (
    <ListRow>
      {children}
      <ListRow.TrailingAsset>
        <DropdownMenu>
          <DropdownMenu.Trigger asChild={true}>
            <IconButton label="Show options" name="Ellipses" spacing="none" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="center" side="bottom">
            {transactionOption && (
              <DropdownMenu.Item key={transactionOption.url} asChild={true}>
                <a href={transactionOption.url}>{transactionOption.title}</a>
              </DropdownMenu.Item>
            )}
            {holdingsOption && (
              <DropdownMenu.Item key={holdingsOption.url} asChild={true}>
                <a href={holdingsOption.url}>{holdingsOption.title}</a>
              </DropdownMenu.Item>
            )}
            {settingsOption && (
              <DropdownMenu.Item key={settingsOption.url} asChild={true}>
                <a href={settingsOption.url}>{settingsOption.title}</a>
              </DropdownMenu.Item>
            )}
          </DropdownMenu.Content>
        </DropdownMenu>
      </ListRow.TrailingAsset>
    </ListRow>
  );
}
